import React, { useEffect, useState, useRef } from "react";
import "./recenttasktab.css";
import { PagedResult } from "../../../../interfaces/PagedResult";
import TaskList from "../../../../interfaces/task/TaskList";
import { useAuth } from "../../../../firebase/AuthProvider";
import { getTasksForParticipant } from "../../../../services/taskServices/TaskService";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Flag } from "lucide-react";
import { Spinner } from "react-bootstrap";
import AvatarImage from "../../../ui/Avatar/AvatarImage";
import "../../hometabs/Hometabs.css";
import { ROUTES } from "../../../../Data/Routes/Routes";
import NoParticipantsFound from "../../participantfiles/noParticipantsFound";
import "../../participantfiles/noParticipantsFound.css";

interface RecenttasktabProps {
  pagination?: { pageNumber: number; pageSize: number };
  setPagination?: React.Dispatch<React.SetStateAction<{ pageNumber: number; pageSize: number }>> | ((pagination: { pageNumber: number; pageSize: number }) => void);
  setTotalItems?: React.Dispatch<React.SetStateAction<number>> | ((total: number) => void);
}

const Recenttasktab: React.FC<RecenttasktabProps> = ({
  pagination: externalPagination,
  setPagination: setExternalPagination,
  setTotalItems: setExternalTotalItems,
}) => {
  const { currentUser, currentOrganization } = useAuth();
  const { id: participantId } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);

  const [participantTasks, setParticipantTasks] =
    useState<PagedResult<TaskList>>();
  const [loading, setLoading] = useState<boolean>(false);

  const [internalPagination, setInternalPagination] = useState<{ pageNumber: number; pageSize: number }>({ pageNumber: 1, pageSize: 10 });
  
  // Use external pagination if provided, otherwise use internal
  const pagination = externalPagination || internalPagination;
  const setPagination = setExternalPagination || setInternalPagination;

  const COMPLETED_STATUSES = ["COMPLETED", "CLOSED", "DELETED"];

  useEffect(() => {
    if (setPagination) {
      setPagination({ pageNumber: 1, pageSize: pagination.pageSize });
    }
  }, [participantId, setPagination, pagination.pageSize]);

  // Determine the border color class
  const isLateStatus = (task: TaskList): string => {
    const statusName = task.status?.name?.toUpperCase() || "";

    // 1. If completed/closed, use 'complete-status' (Green Border)
    if (COMPLETED_STATUSES.includes(statusName)) {
        return "complete-status";
    }

    // 2. Otherwise, check dates for active tasks
    if (isLate(task.endDate)) {
      return "red-status";
    } else {
      // If due in 3 days or less then yellow status
      const now = new Date();
      const warningDate = new Date();
      warningDate.setDate(now.getDate() + 3); 
      
      if (new Date(task.endDate) <= warningDate) {
        return "yellow-status";
      }
    }

    // Default to green-status (no border) if active but safe
    return "green-status";
  };

  const isLate = (dueDate: Date): boolean => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const checkDate = new Date(dueDate);
      checkDate.setHours(0, 0, 0, 0);

      if (checkDate < today) {
        return true;
      } else {
        return false;
      }
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (currentUser) {
      currentUser.getIdToken().then((token) => {
        getParticipantTasks(token);
      });
    }
  }, [currentUser, pagination.pageNumber, pagination.pageSize, participantId]);

  const getParticipantTasks = async (token: string) => {
    if (participantId) {
      try {
        setLoading(true);
        const tasks = await getTasksForParticipant(
          token,
          participantId,
          true,
          pagination.pageNumber,
          pagination.pageSize,
          ["COMPLETED", "CLOSED"]
        );
        if (tasks) {
          const sortedTasks = tasks.items.sort(
            (a, b) =>
              new Date(a.endDate).getTime() - new Date(b.endDate).getTime()
          );
          setParticipantTasks({ ...tasks, items: sortedTasks });
          const totalCount = (tasks as any).totalCount ?? 0;
          if (setExternalTotalItems) {
            setExternalTotalItems(totalCount);
          }
        }
      } catch (error: any) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    }
  }; 

  const badgeBaseClass =
    "text-xs  text-black p-[0.14275rem]  flex items-center justify-center rounded-[6px] whitespace-nowrap";

  const getStatusBadgeClassName = (statusName: string) => {
    const baseClass = "badge-status-custom1";
    const normalizedStatus = statusName.toUpperCase().trim();

    switch (normalizedStatus) {
      case "NOT STARTED":
        return `${baseClass} badge-status-custom-not-started`;
      case "IN REVIEW (SUP)":
        return `${baseClass} badge-status-custom-in-review-sup`;
      case "IN REVIEW (PM)":
      case "IN REVIEW":
        return `${baseClass} badge-status-custom-in-review`;
      case "COMPLETED":
        return `${baseClass} badge-status-custom-completed`;
      case "PENDING EXTENSION":
        return `${baseClass} badge-status-custom-pending-extension`;
      case "EXTENDED":
        return `${baseClass} badge-status-custom-extended`;
      case "READY":
        return `${baseClass} badge-status-custom-ready`;
      case "ISSUES FOUND":
        return `${baseClass} badge-status-custom-issues`;
      case "CLOSED":
        return `${baseClass} badge-status-custom-closed`;
      default:
        return `${baseClass} badge-status-custom-default`;
    }
  };
  
  useEffect(() => {
    if (!containerRef.current) return;

    const calculateMaxWidth = (selector: string) => {
      const badgeElements = containerRef.current!.querySelectorAll(selector);
      if (badgeElements.length === 0) return;

      let maxWidth = 0;

      badgeElements.forEach((el) => {
        const element = el as HTMLElement;
        const originalWidth = element.style.width;
        const originalMinWidth = element.style.minWidth;
        
        element.style.width = 'auto';
        element.style.minWidth = 'auto';
        void element.offsetWidth;
        
        const width = element.scrollWidth || element.offsetWidth;
        
        if (width > maxWidth) {
          maxWidth = width;
        }
        
        element.style.width = originalWidth;
        element.style.minWidth = originalMinWidth;
      });

      if (maxWidth > 0) {
        badgeElements.forEach((el) => {
          const element = el as HTMLElement;
          element.style.width = `${maxWidth}px`;
          element.style.minWidth = `${maxWidth}px`;
        });
      }
    };

    const timeoutId = setTimeout(() => {
      calculateMaxWidth('.badge-text-due');
      calculateMaxWidth('.ps-task-status-new');
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [participantTasks]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner size="sm" /> Loading...
      </div>
    );
  }

  if (!loading && (!participantTasks || participantTasks.items.length === 0)) {
    return (
      <div className="task-list-container ps-tasks-list">
        <div className="no-participants-found-container">
          <NoParticipantsFound />
        </div>
      </div>
    );
  }

  if (!participantTasks) return null;

  return (
    <div ref={containerRef} className="task-list-container ps-tasks-list">
      {participantTasks.items.map((task) => {
        // Determine if task is closed for this render cycle
        const isClosed = COMPLETED_STATUSES.includes(task.status?.name?.toUpperCase() || "");

        return (
          <div
            key={task.id}
            className={`task-card ${isLateStatus(task)}`} 
                onClick={() => {
                    if (!currentOrganization || !task.friendlyId) return;
                    navigate(ROUTES.TASK_DETAILS(currentOrganization?.abbreviation, Number(task?.friendlyId)))
                }}
          >
            <div className="task-card-content">
              <div className="task-info-left">
                <div className="task-assignees">
                  <AvatarImage
                    className="min-w-[20px] min-h-[20px]"
                    userId={""}
                    size={"xs"}
                  />
                </div>
                <div className="task-left1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="task-id top-header">
                      {currentOrganization?.abbreviation}-{task.id}
                    </span>
                    <span className="task-participant top-header">
                      {task.participant?.firstName +
                        " " +
                        task.participant?.lastName}
                    </span>
                  </div>
                  <div className="task-card-body">
                    <h3 className="task-title">{task.title}</h3>
                  </div>
                </div>
              </div>

              <div className="task-info-right">
                <div className="flex items-center gap-[1rem] flex-wrap justify-end">
                  <span className={`task-due-date latedue`}>
                    {isClosed ? (
                      "" // Empty string for closed tasks
                    ) : (
                      <>
                        Due:&nbsp;
                        {(() => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const dueDate = new Date(task.endDate);
                          dueDate.setHours(0, 0, 0, 0);
                          const diffTime = dueDate.getTime() - today.getTime();
                          const diffDays = Math.ceil(
                            diffTime / (1000 * 60 * 60 * 24)
                          );

                          if (diffDays === 0) {
                            return "Today";
                          } else {
                            return `${Math.abs(diffDays)} days`;
                          }
                        })()}
                      </>
                    )}
                  </span>
                  <div className="flex items-center gap-1">
                    <div className="task-assignees">
                      {task.assignees
                        ?.filter((a) => !a.isReporter)
                        .slice(0, 1)
                        .map((a, index, arr) => (
                          <div key={index} className="flex items-center gap-1">
                            <AvatarImage
                              className="max-w-[1.3rem] max-h-[1.3rem]"
                              userId={a.user.id}
                              size={"xs"}
                            />
                            <span className="task-assignees flex items-center gap-1">
                              {a.user.firstName?.[0]}. {a?.user?.lastName}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-[1rem] flex-wrap justify-end">
                  <span
                    className={`${badgeBaseClass} 
                  ${
                    task.subType?.type === "MONTHLY"
                      ? "bg-[#EA9CEB] !important"
                      : task.subType?.type === "QUARTERLY"
                      ? "bg-[#C1F1F1]"
                      : task.subType?.type === "INTERIM"
                      ? "bg-[#9CC1EB] !important"
                      : "bg-[#F6DDD8]"
                  }
                 ps-task-frequency-new badge-text-due `}
                  >
                    {task.subType?.type || "Monthly"}
                  </span>

                  <span
                    className={`${getStatusBadgeClassName(
                      task.status.name
                    )} ps-task-status-new`}
                  >
                    <Flag size={12} />
                    <span>{task.status.name == "READY_" ? "IN PROGRESS" : task.status.name}</span>
                  </span>
                </div>
              </div>
            </div>
           </div>
         );
       })}
    </div>
  );
};

export default Recenttasktab;